import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PlusIcon, ArrowPathIcon, BarsArrowDownIcon } from "@heroicons/react/24/outline";
import { completePhoto, deleteImage, archiveBatch, fetchPhotoIds, emptyEnvironment } from "../api";
import { useSetTopBar } from "../layouts/ShellContext";

// Hooks
import { useLocalStorage, useLocalStorageNumber, useLocalStorageBool } from "../hooks/useLocalStorage";
import { usePhotoStats } from "../hooks/usePhotoStats";
import { usePhotoList, filterPhotos, mapSortToApi, VISIBLE_STATUSES } from "../hooks/usePhotoList";
import { useImageLoader } from "../hooks/useImageLoader";
import { useInfiniteScroll } from "../hooks/useInfiniteScroll";
import { usePermissions } from "../hooks";
import { useUserMatches } from "../features/overview/hooks";
import { STORAGE_KEYS } from "../utils/constants";

// Components
import StatsPills from "../components/overview/StatsPills";
import PhotoCard from "../components/overview/PhotoCard";
import {
  refreshOptions,
  refreshLabel,
  sortOptions,
  sortLabel,
} from "../components/overview/options";
import TimeFilter from "../components/overview/TimeFilter";
import MultiSelectBar from "../components/overview/MultiSelectBar";
import MatchStackCard from "../features/overview/components/MatchStackCard";
import MatchBreadcrumb from "../features/overview/components/MatchBreadcrumb";
import { Btn, Popover } from "../shared/components/redesign";
import LoadingOverlay from "../shared/components/ui/LoadingOverlay";
import EmptyEnvironmentModal from "../components/overview/EmptyEnvironmentModal";

// Constants
const PAGE_SIZE = 100;

const DAY_MS = 24 * 60 * 60 * 1000;

const TIME_PRESETS = [
  { key: 'today', label: 'Today',     days: 1 },
  { key: 'week',  label: 'This week', days: 7 },
  { key: 'month', label: 'Month',     days: 30 },
  { key: 'all',   label: 'All',       days: null },
];

function presetToRange(key) {
  const preset = TIME_PRESETS.find((p) => p.key === key);
  if (!preset || preset.days === null) return null;
  const end = Date.now();
  const start = end - preset.days * DAY_MS;
  return { start, end };
}

function rangeMatchesPreset(range, key) {
  if (key === 'all') return !range;
  if (!range) return false;
  const expected = presetToRange(key);
  if (!expected) return false;
  // Tolerance: ±5 minutes — `expected` re-evaluates Date.now() each render.
  const tol = 5 * 60 * 1000;
  return (
    Math.abs(range.end - expected.end) < tol &&
    Math.abs(range.start - expected.start) < tol
  );
}

export default function Overview({ onNavigate, onTopBadge }) {
  // Permissions
  const {
    canApprovePhotos,
    canArchivePhotos,
    canDeletePhotos,
    canEmptyEnvironment,
    getClickTarget,
  } = usePermissions();

  // Persisted preferences
  const [refreshMs, setRefreshMs] = useLocalStorageNumber("matrix-refresh-ms", 10000, { min: 0 });
  // Full-frame (viz) is the default for all roles; the toggle was removed 2026-04-22.
  const [showViz] = useLocalStorageBool("matrix-show-viz", true);
  const [sortOrder, setSortOrder] = useLocalStorage("matrix-sort-order", "oldest");
  const [activeFilters, setActiveFilters] = useLocalStorage("matrix-active-filters", []);
  const [timeRange, setTimeRange] = useLocalStorage("matrix-time-range", null);
  const [showTimeSlider, setShowTimeSlider] = useLocalStorageBool("matrix-show-time-slider", false);
  const [matchGrouping, setMatchGrouping] = useLocalStorage(STORAGE_KEYS.MATCH_GROUPING, "none");
  const [gridLayout, setGridLayout] = useLocalStorage("matrix-grid-layout", "masonry");

  // Masonry column count, derived from `window.innerWidth` minus the
  // TankShell left rail (56 px, --rd-chrome-rail) and `.thumb-grid`'s
  // horizontal padding (60+60). `window.innerWidth` is reliable on
  // first paint — `el.clientWidth` was not (the cold-mount race that
  // Phase 8 worked around with CSS multi-column). Eager initialiser
  // means the first render sees the correct value; the resize
  // listener handles viewport changes thereafter.
  const computeMasonryCols = () => {
    if (typeof window === 'undefined') return 4;
    const TARGET_CARD_WIDTH = 340;
    const RAIL_WIDTH = 56;
    const GRID_PADDING = 120;
    const w = window.innerWidth - RAIL_WIDTH - GRID_PADDING;
    return Math.max(1, Math.floor(w / TARGET_CARD_WIDTH));
  };
  const [masonryCols, setMasonryCols] = useState(computeMasonryCols);
  useEffect(() => {
    const handler = () => setMasonryCols(computeMasonryCols());
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Local state
  const [search, setSearch] = useState("");
  const [multiMode, setMultiMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [markingDone, setMarkingDone] = useState(false);
  const [deletingPhotos, setDeletingPhotos] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [archivingPhotos, setArchivingPhotos] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [showEmptyEnvModal, setShowEmptyEnvModal] = useState(false);

  // Bulk operation state for loading overlay
  const [bulkOperation, setBulkOperation] = useState({
    active: false,
    type: null, // 'delete' | 'archive' | 'markDone'
    total: 0,
    completed: 0,
  });
  const bulkCancelRef = useRef(false);

  // Layout refs
  const statsRef = useRef(null);
  const gridRef = useRef(null);

  // Custom hooks
  const imageLoader = useImageLoader();
  const { stats, totals, metrics, rateLabel, ingestionStats } = usePhotoStats(refreshMs);
  const { matches, liveMatches, isMatchLive } = useUserMatches();

  const {
    photos,
    loading,
    loadingMore,
    hasMore,
    error,
    loadMore,
    refresh: refreshPhotos,
    totalCount: serverTotalCount,
  } = usePhotoList({
    activeFilters,
    sortOrder,
    search,
    refreshMs,
    imageErrorsRef: imageLoader.errorsRef,
    // No pill selected → still ask backend for visible-only statuses, so
    // the grid isn't drowned by page-1 of processing-group photos.
    defaultStatus: VISIBLE_STATUSES.join(","),
  });

  const { sentinelRef } = useInfiniteScroll({
    hasMore,
    loading: loadingMore,
    onLoadMore: loadMore,
    enabled: photos.length > 0,
  });

  // Computed values
  const listPhotos = useMemo(
    () => filterPhotos(photos, activeFilters, timeRange),
    [photos, activeFilters, timeRange]
  );

  const hasTimeData = useMemo(
    () => photos.some((p) => p.capture_date),
    [photos]
  );

  // Clear selectedMatch when switching grouping modes
  useEffect(() => {
    setSelectedMatch(null);
  }, [matchGrouping]);

  // Filter photos for "my_match" mode - only show photos from live matches
  const matchFilteredPhotos = useMemo(() => {
    if (matchGrouping === 'my_match') {
      if (liveMatches.length === 0) {
        return listPhotos; // Show all if no live matches
      }
      const liveMatchIds = new Set(liveMatches.map((m) => m.id));
      return listPhotos.filter((p) => p.match && liveMatchIds.has(p.match));
    }
    if (matchGrouping === 'by_match' && selectedMatch !== null) {
      // Filter by selected match (null match ID = unassigned)
      if (selectedMatch === 'unassigned') {
        return listPhotos.filter((p) => !p.match);
      }
      return listPhotos.filter((p) => p.match === selectedMatch);
    }
    return listPhotos;
  }, [matchGrouping, listPhotos, liveMatches, selectedMatch]);

  // Group photos by match when in "by_match" mode
  const photosByMatch = useMemo(() => {
    if (matchGrouping !== 'by_match' || selectedMatch !== null) return null;

    const grouped = {};
    listPhotos.forEach((photo) => {
      const matchId = photo.match || 'unassigned';
      if (!grouped[matchId]) {
        grouped[matchId] = [];
      }
      grouped[matchId].push(photo);
    });
    return grouped;
  }, [matchGrouping, listPhotos, selectedMatch]);

  // Get match details by ID for displaying in stack cards
  const getMatchById = useCallback(
    (matchId) => matches.find((m) => m.id === matchId),
    [matches]
  );

  // Get selected match details for breadcrumb
  const selectedMatchDetails = useMemo(() => {
    if (selectedMatch === 'unassigned') return null;
    return getMatchById(selectedMatch);
  }, [selectedMatch, getMatchById]);

  // Wire the redesign top-bar with a contextual breadcrumb + throughput rate.
  const setTopBar = useSetTopBar();
  useEffect(() => {
    let breadcrumb = null;
    let label = null;
    if (selectedMatch === 'unassigned') {
      label = 'Unassigned photos';
    } else if (selectedMatchDetails) {
      const home = selectedMatchDetails.home_team_name;
      const away = selectedMatchDetails.away_team_name;
      const teams = home && away
        ? `${home} – ${away}`
        : selectedMatchDetails.name || 'Match';
      const comp = selectedMatchDetails.competition || '';
      label = comp ? `${comp} · ${teams}` : teams;
    }

    if (label) {
      // When drilled into a specific match in by_match mode, prefix the
      // breadcrumb with a clickable "← All matches" so the back affordance
      // lives in the top bar (replaces the deleted in-page MatchBreadcrumb).
      breadcrumb = (
        <>
          <button
            type="button"
            onClick={() => setSelectedMatch(null)}
            style={{
              background: 'transparent',
              border: 0,
              padding: 0,
              cursor: 'pointer',
              color: 'var(--rd-color-text-subtle)',
              font: 'inherit',
            }}
            title="Back to all matches"
          >
            ← All matches
          </button>
          <span style={{ color: 'var(--rd-color-text-faint)', margin: '0 6px' }}>/</span>
          <span>{label}</span>
        </>
      );
    }

    setTopBar({
      breadcrumb,
      rate: rateLabel || null,
      kbHints: [
        { keys: ['/'], label: 'search' },
        { keys: ['J', 'K'], label: 'navigate' },
        { keys: ['↵'], label: 'open' },
      ],
    });
    return () => setTopBar(null);
  }, [selectedMatch, selectedMatchDetails, rateLabel, setTopBar]);

  // URL helpers
  const fileBase = useMemo(() => {
    if (typeof window === "undefined") return "";
    const api = import.meta.env.VITE_OPERATOR_API || "/api";
    return api.replace(/\/api\/?$/, "");
  }, []);

  const resolveUrl = useCallback(
    (url) => {
      if (!url) return "";
      if (/^https?:\/\//i.test(url)) return url;
      const sep = url.startsWith("/") ? "" : "/";
      return `${fileBase}${sep}${url}`;
    },
    [fileBase]
  );

  const withCache = useCallback((url, cacheKey) => {
    if (!url) return "";
    if (!cacheKey) return url;
    const join = url.includes("?") ? "&" : "?";
    return `${url}${join}v=${encodeURIComponent(cacheKey)}`;
  }, []);

  const cacheKeyFor = useCallback((p) => `${p.status}-${p.__cache || p.updated_at || "0"}`, []);


  // Update top badge with rate
  useEffect(() => {
    if (typeof onTopBadge === "function") {
      onTopBadge(rateLabel || "");
      return () => onTopBadge("");
    }
    return undefined;
  }, [rateLabel, onTopBadge]);

  // Filter handlers
  const toggleFilter = useCallback((key) => {
    setActiveFilters((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  }, [setActiveFilters]);

  const clearFilters = useCallback(() => setActiveFilters([]), [setActiveFilters]);
  const clearTimeRange = useCallback(() => setTimeRange(null), [setTimeRange]);

  // Multi-select handlers
  const toggleMultiMode = useCallback(() => {
    setMultiMode((prev) => {
      if (prev) setSelectedIds(new Set());
      return !prev;
    });
  }, []);

  const toggleSelection = useCallback((imageId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(imageId)) {
        next.delete(imageId);
      } else {
        next.add(imageId);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback(async () => {
    // If more photos exist than currently loaded, fetch all matching IDs from server
    if (serverTotalCount > listPhotos.length) {
      setSelectingAll(true);
      try {
        // Build params matching usePhotoList filter logic
        const params = {};
        if (activeFilters.includes("unlabeled")) {
          params.has_unlabeled_faces = true;
        }
        if (activeFilters.includes("done")) {
          params.status = "done,exported";
        }
        if (activeFilters.includes("attention")) {
          params.attention = true;
        }
        if (search) {
          params.q = search;
        }
        if (sortOrder) {
          params.ordering = mapSortToApi(sortOrder);
        }

        const { ids } = await fetchPhotoIds(params);
        setSelectedIds(new Set(ids));
      } catch (e) {
        console.error("Failed to fetch all IDs:", e);
        // Fallback to just loaded photos
        setSelectedIds(new Set(listPhotos.map((p) => p.image_id)));
      } finally {
        setSelectingAll(false);
      }
    } else {
      // All photos are loaded, use them directly
      setSelectedIds(new Set(listPhotos.map((p) => p.image_id)));
    }
  }, [listPhotos, serverTotalCount, activeFilters, search, sortOrder]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // Batch action handlers - process in chunks to avoid browser connection limits
  const BATCH_SIZE = 10;

  const handleMarkDone = useCallback(async () => {
    if (selectedIds.size === 0 || markingDone) return;
    setMarkingDone(true);
    bulkCancelRef.current = false;

    const ids = Array.from(selectedIds);
    const total = ids.length;

    // Show loading overlay for bulk operations (3+ items)
    if (total >= 3) {
      setBulkOperation({ active: true, type: 'markDone', total, completed: 0 });
    }

    try {
      let completed = 0;
      for (let i = 0; i < ids.length; i += BATCH_SIZE) {
        if (bulkCancelRef.current) break;
        const batch = ids.slice(i, i + BATCH_SIZE);
        await Promise.all(batch.map((id) => completePhoto(id)));
        completed += batch.length;
        if (total >= 3) {
          setBulkOperation((prev) => ({ ...prev, completed }));
        }
      }

      if (!bulkCancelRef.current) {
        setSelectedIds(new Set());
        setMultiMode(false);
      }
      refreshPhotos();
    } catch (e) {
      console.error("Failed to mark done:", e);
    } finally {
      setMarkingDone(false);
      setBulkOperation({ active: false, type: null, total: 0, completed: 0 });
    }
  }, [selectedIds, markingDone, refreshPhotos]);

  const handleDelete = useCallback(() => {
    if (selectedIds.size === 0 || deletingPhotos) return;
    setShowDeleteConfirm(true);
  }, [selectedIds, deletingPhotos]);

  const confirmDelete = useCallback(async () => {
    setShowDeleteConfirm(false);
    setDeletingPhotos(true);
    bulkCancelRef.current = false;

    const ids = Array.from(selectedIds);
    const total = ids.length;

    // Show loading overlay for bulk operations (3+ items)
    if (total >= 3) {
      setBulkOperation({ active: true, type: 'delete', total, completed: 0 });
    }

    try {
      let completed = 0;
      const processedIds = [];
      for (let i = 0; i < ids.length; i += BATCH_SIZE) {
        if (bulkCancelRef.current) break;
        const batch = ids.slice(i, i + BATCH_SIZE);
        await Promise.all(batch.map((id) => deleteImage(id)));
        processedIds.push(...batch);
        completed += batch.length;
        if (total >= 3) {
          setBulkOperation((prev) => ({ ...prev, completed }));
        }
      }

      if (bulkCancelRef.current) {
        // Keep remaining items selected for retry
        const processedSet = new Set(processedIds);
        setSelectedIds((prev) => new Set([...prev].filter((id) => !processedSet.has(id))));
      } else {
        setSelectedIds(new Set());
        setMultiMode(false);
      }
      refreshPhotos();
    } catch (e) {
      console.error("Failed to delete:", e);
    } finally {
      setDeletingPhotos(false);
      setBulkOperation({ active: false, type: null, total: 0, completed: 0 });
    }
  }, [selectedIds, refreshPhotos]);

  const handleArchive = useCallback(() => {
    if (selectedIds.size === 0 || archivingPhotos) return;
    setShowArchiveConfirm(true);
  }, [selectedIds, archivingPhotos]);

  const confirmArchive = useCallback(async () => {
    setShowArchiveConfirm(false);
    setArchivingPhotos(true);
    bulkCancelRef.current = false;

    const ids = Array.from(selectedIds);
    const total = ids.length;

    // Show loading overlay for bulk operations (3+ items)
    // Archive is a single API call, so we show indeterminate progress
    if (total >= 3) {
      setBulkOperation({ active: true, type: 'archive', total, completed: 0 });
    }

    try {
      await archiveBatch(ids);
      setSelectedIds(new Set());
      setMultiMode(false);
      refreshPhotos();
    } catch (e) {
      console.error("Failed to archive:", e);
    } finally {
      setArchivingPhotos(false);
      setBulkOperation({ active: false, type: null, total: 0, completed: 0 });
    }
  }, [selectedIds, refreshPhotos]);

  // Helper function to get operation message for loading overlay
  const getOperationMessage = useCallback((type) => {
    switch (type) {
      case 'delete':
        return 'Deleting photos...';
      case 'archive':
        return 'Archiving photos...';
      case 'markDone':
        return 'Marking photos as done...';
      default:
        return 'Processing...';
    }
  }, []);

  // Cancel handler for bulk operations
  const handleCancelBulkOperation = useCallback(() => {
    bulkCancelRef.current = true;
  }, []);

  // Empty environment handler
  const handleEmptyEnvironment = useCallback(async ({ force = false } = {}) => {
    try {
      await emptyEnvironment({ force });
      setShowEmptyEnvModal(false);
      setMultiMode(false);
      setSelectedIds(new Set());
      refreshPhotos();
    } catch (e) {
      console.error('Failed to empty environment:', e);
      throw e; // Let modal handle the error display
    }
  }, [refreshPhotos]);

  return (
    <div className="page page-overview">
      <div className="overview-header">
        <div ref={statsRef}>
          <StatsPills
            totals={totals}
            activeFilters={activeFilters}
            onToggle={toggleFilter}
            onClear={clearFilters}
            metrics={metrics}
            ingestionStats={ingestionStats}
          />
        </div>

        {multiMode ? (
          <div className="filters">
            <MultiSelectBar
              selectedCount={selectedIds.size}
              loadedCount={listPhotos.length}
              totalCount={serverTotalCount}
              hasMore={hasMore}
              onCancel={toggleMultiMode}
              onMarkDone={handleMarkDone}
              onArchive={handleArchive}
              onDelete={handleDelete}
              onSelectAll={selectAll}
              onClearSelection={clearSelection}
              markingDone={markingDone}
              archivingPhotos={archivingPhotos}
              deletingPhotos={deletingPhotos}
              selectingAll={selectingAll}
              showArchive={activeFilters.includes("done")}
              canMarkDone={canApprovePhotos}
              canArchive={canArchivePhotos}
              canDelete={canDeletePhotos}
              canEmptyEnvironment={canEmptyEnvironment}
              onEmptyEnvironment={() => setShowEmptyEnvModal(true)}
            />
          </div>
        ) : (
          <>
            {/* Filter row: search · time presets · match grouping */}
            <div className="filters filters-row">
              <input
                className="search"
                placeholder="Search filename or id"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <span className="filter-divider" aria-hidden="true" />
              <span className="filter-label">Time</span>
              {TIME_PRESETS.map((p) => {
                const active = rangeMatchesPreset(timeRange, p.key);
                return (
                  <button
                    key={p.key}
                    type="button"
                    className={`chip ${active ? "active" : ""}`}
                    onClick={() => {
                      if (p.key === 'all') clearTimeRange();
                      else setTimeRange(presetToRange(p.key));
                    }}
                  >
                    {p.label}
                  </button>
                );
              })}
              {hasTimeData && (
                <button
                  type="button"
                  className={`chip ${showTimeSlider ? "active" : ""}`}
                  onClick={() => setShowTimeSlider((v) => !v)}
                  title={showTimeSlider ? "Hide time slider" : "Custom time range"}
                >
                  Custom
                </button>
              )}
              <span className="filter-divider" aria-hidden="true" />
              <button
                type="button"
                className={`chip ${matchGrouping === 'my_match' ? 'active' : ''}`}
                onClick={() => setMatchGrouping(matchGrouping === 'my_match' ? 'none' : 'my_match')}
                title="Show only photos from your live matches"
              >
                My match
              </button>
              <button
                type="button"
                className={`chip ${matchGrouping === 'by_match' ? 'active' : ''}`}
                onClick={() => setMatchGrouping(matchGrouping === 'by_match' ? 'none' : 'by_match')}
                title="Group photos by match"
              >
                By match
              </button>
              {(activeFilters.length > 0 || !!timeRange) && (
                <button
                  type="button"
                  className="chip"
                  onClick={() => { clearFilters(); clearTimeRange(); }}
                  title="Clear active filters"
                >
                  × Clear filters
                </button>
              )}
            </div>

            {/* Action row: refresh · sort · multi (right-aligned) */}
            <div className="filters filters-actions">
              <Popover
                trigger={
                  <Btn
                    kind="ghost"
                    size="sm"
                    icon={<ArrowPathIcon style={{ width: 12, height: 12 }} />}
                  >
                    Auto · {refreshLabel(refreshMs)}
                  </Btn>
                }
              >
                {refreshOptions.map((opt) => (
                  <Popover.Item
                    key={opt.value}
                    selected={refreshMs === opt.value}
                    onSelect={() => setRefreshMs(opt.value)}
                  >
                    {opt.label}
                  </Popover.Item>
                ))}
              </Popover>
              <Popover
                trigger={
                  <Btn
                    kind="ghost"
                    size="sm"
                    icon={<BarsArrowDownIcon style={{ width: 12, height: 12 }} />}
                  >
                    Sort: {sortLabel(sortOrder)}
                  </Btn>
                }
              >
                {sortOptions.map((opt) => (
                  <Popover.Item
                    key={opt.value}
                    selected={sortOrder === opt.value}
                    onSelect={() => setSortOrder(opt.value)}
                  >
                    {opt.label}
                  </Popover.Item>
                ))}
              </Popover>
              <div className="push-right">
                <button
                  type="button"
                  className={`chip ${gridLayout === 'masonry' ? 'multi-active' : ''}`}
                  onClick={() => setGridLayout(gridLayout === 'masonry' ? 'fit' : 'masonry')}
                  title={gridLayout === 'masonry' ? 'Switch to fitted (4:3) grid' : 'Switch to masonry (natural aspect)'}
                >
                  {gridLayout === 'masonry' ? 'Masonry' : 'Fit'}
                </button>
                <button
                  type="button"
                  className={`chip ${multiMode ? "multi-active" : ""}`}
                  onClick={toggleMultiMode}
                  title="Enter multi-select mode"
                >
                  <PlusIcon className="icon-sm" style={{ marginRight: 4 }} />
                  Multi
                </button>
              </div>
            </div>

            {/* TimeFilter slider — toggleable custom range */}
            {showTimeSlider && (
              <TimeFilter
                photos={photos}
                timeRange={timeRange}
                onChange={setTimeRange}
                onClear={clearTimeRange}
              />
            )}
          </>
        )}
      </div>

      {error && <div className="error-text">{error}</div>}

      {/* My Match mode — show subtle banner if no live matches.
          Uses the redesign panel/eyebrow tokens instead of legacy inline blue. */}
      {matchGrouping === 'my_match' && liveMatches.length === 0 && (
        <div className="overview-banner">
          <span className="overview-banner-eyebrow">No live matches</span>
          <span className="overview-banner-body">Showing all photos.</span>
        </div>
      )}

      {matchFilteredPhotos.length === 0 && !loading ? (
        <div className="empty-state">
          <div className="empty-message">Waiting for photos...</div>
          <div className="empty-sub">New photos will appear automatically.</div>
        </div>
      ) : matchGrouping === 'by_match' && photosByMatch && selectedMatch === null ? (
        /* Show match stacks when in by_match mode and no match selected */
        <div className="thumb-grid" ref={gridRef} style={{ '--card-min-width': '280px' }}>
          {Object.entries(photosByMatch).map(([matchId, matchPhotos]) => {
            const match = matchId === 'unassigned' ? null : getMatchById(matchId);
            const statusCounts = {
              pending: matchPhotos.filter((p) => p.status === 'pending' || p.status === 'processing').length,
              done: matchPhotos.filter((p) => p.status === 'done' || p.status === 'exported').length,
            };
            const previewUrls = matchPhotos.slice(0, 3).map((p) =>
              resolveUrl(withCache(p.thumb_url, cacheKeyFor(p)))
            );
            return (
              <MatchStackCard
                key={matchId}
                match={match}
                photoCount={matchPhotos.length}
                statusCounts={statusCounts}
                isLive={matchId !== 'unassigned' && isMatchLive(matchId)}
                isUnassigned={matchId === 'unassigned'}
                previewUrls={previewUrls}
                onClick={() => setSelectedMatch(matchId)}
              />
            );
          })}
        </div>
      ) : gridLayout === 'masonry' ? (
        <MasonryGrid
          ref={gridRef}
          photos={matchFilteredPhotos}
          cols={masonryCols}
          showViz={showViz}
          multiMode={multiMode}
          selectedIds={selectedIds}
          toggleSelection={toggleSelection}
          onNavigate={onNavigate}
          imageLoader={imageLoader}
          activeFilters={activeFilters}
          resolveUrl={resolveUrl}
          withCache={withCache}
          cacheKeyFor={cacheKeyFor}
          getClickTarget={getClickTarget}
          sentinelRef={sentinelRef}
        />
      ) : (
        <div className="thumb-grid" ref={gridRef}>
          {matchFilteredPhotos.map((p) => (
            <PhotoCard
              key={p.image_id}
              photo={p}
              showViz={showViz}
              multiMode={multiMode}
              isSelected={selectedIds.has(p.image_id)}
              onSelect={toggleSelection}
              onNavigate={onNavigate}
              imageLoader={imageLoader}
              activeFilters={activeFilters}
              resolveUrl={resolveUrl}
              withCache={withCache}
              cacheKeyFor={cacheKeyFor}
              getClickTarget={getClickTarget}
              naturalAspect={false}
            />
          ))}

          {/* Sentinel for infinite scroll */}
          <div
            ref={sentinelRef}
            style={{ height: 20, width: "100%", gridColumn: "1 / -1" }}
          />

          {loadingMore && (
            <div
              className="load-more-indicator"
              style={{ gridColumn: "1 / -1", textAlign: "center", padding: "20px 0" }}
            >
              <span style={{ color: "#66ffb7", opacity: 0.8 }}>Loading more...</span>
            </div>
          )}

          {!hasMore && matchFilteredPhotos.length > 0 && matchFilteredPhotos.length >= PAGE_SIZE && (
            <div
              style={{
                gridColumn: "1 / -1",
                textAlign: "center",
                padding: "16px 0",
                color: "#666",
                fontSize: 13,
              }}
            >
              Showing all {matchFilteredPhotos.length} photos
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={() => setShowDeleteConfirm(false)}
        >
          <div
            style={{
              background: "#1a1a1a",
              border: "1px solid #333",
              borderRadius: 12,
              padding: "24px 32px",
              maxWidth: 400,
              textAlign: "center",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: 32, marginBottom: 16, color: "#dc3545", fontWeight: "bold" }}>DELETE</div>
            <h3 style={{ margin: "0 0 12px", color: "#fff" }}>
              Delete {selectedIds.size} photo{selectedIds.size !== 1 ? "s" : ""}?
            </h3>
            <p style={{ color: "#888", margin: "0 0 24px", fontSize: 14 }}>
              This action cannot be undone. The photos will be permanently removed.
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                style={{
                  padding: "10px 24px",
                  background: "#333",
                  border: "none",
                  borderRadius: 6,
                  color: "#fff",
                  cursor: "pointer",
                  fontSize: 14,
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                style={{
                  padding: "10px 24px",
                  background: "#dc3545",
                  border: "none",
                  borderRadius: 6,
                  color: "#fff",
                  cursor: "pointer",
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Archive Confirmation Modal */}
      {showArchiveConfirm && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={() => setShowArchiveConfirm(false)}
        >
          <div
            style={{
              background: "#1a1a1a",
              border: "1px solid #333",
              borderRadius: 12,
              padding: "24px 32px",
              maxWidth: 400,
              textAlign: "center",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: 32, marginBottom: 16, color: "#66b7ff", fontWeight: "bold" }}>ARCHIVE</div>
            <h3 style={{ margin: "0 0 12px", color: "#fff" }}>
              Archive {selectedIds.size} photo{selectedIds.size !== 1 ? "s" : ""}?
            </h3>
            <p style={{ color: "#888", margin: "0 0 24px", fontSize: 14 }}>
              Photos will be archived to a zip file. Original files will be removed
              from central storage, but thumbnails and metadata will remain for
              viewing.
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <button
                onClick={() => setShowArchiveConfirm(false)}
                style={{
                  padding: "10px 24px",
                  background: "#333",
                  border: "none",
                  borderRadius: 6,
                  color: "#fff",
                  cursor: "pointer",
                  fontSize: 14,
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmArchive}
                style={{
                  padding: "10px 24px",
                  background: "#a29bff",
                  border: "none",
                  borderRadius: 6,
                  color: "#000",
                  cursor: "pointer",
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                Archive
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Empty Environment Modal */}
      {showEmptyEnvModal && (
        <EmptyEnvironmentModal
          isOpen={showEmptyEnvModal}
          onClose={() => setShowEmptyEnvModal(false)}
          onConfirm={handleEmptyEnvironment}
          stats={stats}
        />
      )}

      {/* Bulk Operation Loading Overlay */}
      <LoadingOverlay
        isVisible={bulkOperation.active}
        message={getOperationMessage(bulkOperation.type)}
        completed={bulkOperation.completed}
        total={bulkOperation.total}
        onCancel={bulkOperation.type !== 'archive' ? handleCancelBulkOperation : undefined}
      />
    </div>
  );
}

/**
 * MasonryGrid — shortest-column-first photo distribution.
 *
 * Iterating photos in DOM order and placing each in the currently
 * shortest column yields **row-major** flow: newest goes to col 1,
 * second-newest to col 2, third to col 3 (assuming roughly even
 * heights so far), etc. This matches the editor's L→R review
 * pattern. For varied heights the algorithm approximates row-major
 * (the column with most space wins, which usually rotates).
 *
 * Card heights are estimated from `photo.crop_info.{image_width,
 * image_height}` plus a fixed meta-row constant. Estimation is
 * intentionally cheap — it only needs to decide WHICH column each
 * card lands in, not the exact height. The card itself uses
 * `measuredAspect` from `<img>.naturalWidth/Height` (Phase 7a-iii)
 * for the rendered aspect-ratio, so portraits look correct even when
 * crop_info reports raw landscape sensor dims.
 */
const META_ROW_PX = 84;            // measured .thumb-meta height (filename + subline + padding)
const ASSUMED_CARD_WIDTH = 340;    // matches the column-pick TARGET_CARD_WIDTH
const FALLBACK_ASPECT = 3 / 2;     // assume landscape when crop_info missing

const MasonryGrid = forwardRef(function MasonryGrid(
  {
    photos,
    cols,
    showViz,
    multiMode,
    selectedIds,
    toggleSelection,
    onNavigate,
    imageLoader,
    activeFilters,
    resolveUrl,
    withCache,
    cacheKeyFor,
    getClickTarget,
    sentinelRef,
  },
  ref,
) {
  const buckets = useMemo(() => {
    const heights = new Array(cols).fill(0);
    const out = Array.from({ length: cols }, () => []);
    for (const p of photos) {
      const w = p.crop_info?.image_width;
      const h = p.crop_info?.image_height;
      const aspect = w && h ? w / h : FALLBACK_ASPECT;
      const imageHeight = ASSUMED_CARD_WIDTH / aspect;
      const estHeight = imageHeight + META_ROW_PX;
      let target = 0;
      for (let i = 1; i < cols; i++) {
        if (heights[i] < heights[target]) target = i;
      }
      out[target].push(p);
      heights[target] += estHeight;
    }
    return out;
  }, [photos, cols]);

  return (
    <div className="thumb-grid thumb-grid--masonry" ref={ref}>
      {buckets.map((colPhotos, colIdx) => (
        <div key={colIdx} className="masonry-col">
          {colPhotos.map((p) => (
            <PhotoCard
              key={p.image_id}
              photo={p}
              showViz={showViz}
              multiMode={multiMode}
              isSelected={selectedIds.has(p.image_id)}
              onSelect={toggleSelection}
              onNavigate={onNavigate}
              imageLoader={imageLoader}
              activeFilters={activeFilters}
              resolveUrl={resolveUrl}
              withCache={withCache}
              cacheKeyFor={cacheKeyFor}
              getClickTarget={getClickTarget}
              naturalAspect
            />
          ))}
        </div>
      ))}

      {/* Sentinel for infinite scroll. Spans the grid so the scroller
          can hit it in any column. */}
      <div
        ref={sentinelRef}
        style={{ position: 'absolute', bottom: 0, height: 20, width: '100%' }}
      />
    </div>
  );
});
